/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { Button } from 'src/components/core/buttons/button';
import { Content } from '@prismicio/client';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { MorePagesSection } from 'src/components/sections/more-pages-section';
import { PrismicRichText, PrismicRichTextWrapper } from 'src/components/prismic';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Section } from 'src/components/core/layout/section';
import { Text } from 'src/components/core/text';
import { createPrimsicClient } from 'src/prismic/client';
import { normalizePrismicSEO } from 'src/prismic/utils';
import { routeResolve } from 'src/core/utils/routes';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslation } from 'next-i18next';
import { withSettings } from 'src/hocs/with-settings';
import chevronLeftSvg from 'src/assets/svgs/chevron-left.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: Content.LegalDocumentData;
  legalPagesData: Array<{
    slug: Content.LegalDocument['uid'];
    title: string;
  }>;
};

/**
 * `StyledSection` styled component.
 */

const StyledSection = styled(Section)`
  --section-padding-top: var(--space-sm);
`;

/**
 * `LegalDetails` page.
 */

const LegalDetails: NextPage<Props> = ({ data, legalPagesData }) => {
  const { t } = useTranslation();
  const isMobile = useBreakpoint(0, 'md');

  return (
    <>
      <SEO {...normalizePrismicSEO(data)} />

      <AnimationController staggerMobile>
        <StyledSection hasNavbarOffset>
          <Grid>
            <SubGrid style={{ paddingInline: isMobile ? 'var(--space-sm)' : 0 }}>
              <FadeInUp>
                <Button
                  aria-label={t('common:actions.back')}
                  color={'var(--color-slate50)'}
                  colorTheme={'text'}
                  href={routeResolve('legal')}
                  icon={chevronLeftSvg}
                  size={'medium'}
                >
                  <Text color={'var(--color-slate50)'} variant={'labelL'}>
                    {t('common:actions.back')}
                  </Text>
                </Button>
              </FadeInUp>

              <Heading
                asTitle={'h1'}
                description={<PrismicRichText field={data?.description} />}
                descriptionTheme={'slate100'}
                lead={data?.lead as string}
                style={{ marginBottom: 'var(--space-ms)', paddingTop: '48px' }}
                title={data?.title as string}
              />

              <FadeInUp>
                <PrismicRichTextWrapper>
                  <PrismicRichText field={data?.body} />
                </PrismicRichTextWrapper>
              </FadeInUp>
            </SubGrid>
          </Grid>
        </StyledSection>

        <FadeInUp triggerInView>
          <MorePagesSection
            data={legalPagesData?.map(({ slug, title }) => ({
              href: routeResolve('legalDetails', { slug }),
              title
            }))}
          />
        </FadeInUp>
      </AnimationController>
    </>
  );
};

/**
 * Export `getStaticPaths`.
 */

export const getStaticPaths: GetStaticPaths = async () => {
  const client = createPrimsicClient();
  const categories = await client.getAllByType('legal');

  return {
    fallback: false,
    paths: categories.map(({ uid }) => ({
      params: {
        slug: uid
      }
    }))
  };
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = withSettings<any>(async ctx => {
  const { locale, params, previewData } = ctx;
  const slug = params?.slug as string;
  const client = createPrimsicClient({ previewData });
  const legalpagesData = await client.getAllByType('legal', {
    orderings: {
      direction: 'asc',
      field: 'document.first_publication_date'
    }
  });

  const { data } = await client.getByUID('legal', slug);

  return {
    props: {
      ...(await serverSideTranslations(locale as string, ['common', 'legal'])),
      data,
      legalPagesData: legalpagesData
        .filter(page => page.uid !== slug)
        .map(({ data, uid }) => ({ slug: uid, title: data.title }))
    },
    revalidate: 60
  };
});

/**
 * Export `LegalDetails` page.
 */

export default LegalDetails;
